import { useCallback } from "react";
import {
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { listProjects } from "../api";
import { projectQueryKeys } from "../query-keys";
import {
  PERSONAL_PROJECT_SCOPE,
  type ProjectListParams,
  type ProjectListScope,
} from "../types";

type UseProjectsQueryOptions = {
  enabled?: boolean;
};

const FIVE_MINUTES = 5 * 60 * 1000;

export const invalidateProjectsQueryScope = (
  queryClient: QueryClient,
  scope: ProjectListScope,
) => {
  if (!scope.principalId) return Promise.resolve();

  return queryClient.invalidateQueries({
    queryKey: projectQueryKeys.listScope(scope),
  });
};

export const useProjectsQueryScope = () => {
  const { currentUser } = useAuth();
  const { currentOrganization, initialized } = useOrganization();

  return {
    scope: {
      principalId: currentUser?.id ?? "",
      organizationId: currentOrganization?.id ?? PERSONAL_PROJECT_SCOPE,
    } satisfies ProjectListScope,
    initialized,
  };
};

export const useProjectsQuery = (
  params: ProjectListParams = {},
  { enabled = true }: UseProjectsQueryOptions = {},
) => {
  const { scope, initialized } = useProjectsQueryScope();

  return useQuery({
    queryKey: projectQueryKeys.list(scope, params),
    queryFn: () =>
      listProjects({ organizationId: scope.organizationId, params }),
    enabled: enabled && initialized === true && Boolean(scope.principalId),
    staleTime: FIVE_MINUTES,
  });
};

export const useInvalidateProjectsQuery = () => {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const { currentOrganization } = useOrganization();
  const principalId = currentUser?.id;
  const organizationId =
    currentOrganization?.id ?? PERSONAL_PROJECT_SCOPE;

  return useCallback(() => {
    return invalidateProjectsQueryScope(queryClient, {
      principalId: principalId ?? "",
      organizationId,
    });
  }, [organizationId, principalId, queryClient]);
};
